import { serialiseValue } from '@/app-shell/flags/flag-evaluation-telemetry';
import { logger } from '@/utils/logger/logger';
import {
  BaseEvaluationStrategy,
  ErrorCode,
  type EvaluationContext,
  type FinalResult,
  FlagNotFoundError,
  type FlagValue,
  type Hook,
  MultiProvider,
  type Provider,
  type ProviderEntryInput,
  type ProviderResolutionResult,
  type ProviderResolutionSuccessResult,
  ProviderStatus,
  type StrategyEvaluationContext,
  type StrategyPerProviderContext,
  throwAggregateErrorFromPromiseResults,
} from '@openfeature/web-sdk';
import { isEqual } from 'lodash-es';

type ProviderResult<T extends FlagValue> = ProviderResolutionResult<T>;
type HealthyResult<T extends FlagValue> = ProviderResolutionSuccessResult<T>;
type NamedProviderEntry = ProviderEntryInput<Provider> & { name: string };

type ComparisonOutcome = {
  agreement?: boolean;
  mismatch?: boolean;
  failedProvider?: string;
  failedCode?: string;
  shadowValue?: string;
  shadowReason?: string;
  shadowPending?: boolean;
};

export const FLAG_AUTHORITY_HINT = 'flagAuthority';

export function createCustomComparisonMultiProvider(options: {
  authorities: [NamedProviderEntry, ...NamedProviderEntry[]];
  shadow?: NamedProviderEntry;
}): MultiProvider {
  const { authorities, shadow } = options;
  const authorityNames = authorities.map((a) => a.name);
  const warnedHints = new Set<string>();
  const declaredAuthority = new WeakMap<EvaluationContext, string>();
  const entries = [...authorities, ...(shadow ? [shadow] : [])];
  class ComparisonMultiProvider extends MultiProvider {
    override async onContextChange(
      oldContext: EvaluationContext,
      newContext: EvaluationContext
    ): Promise<void> {
      const results = await Promise.allSettled(
        this.providerEntries.map(async ({ provider }) =>
          provider.onContextChange?.(oldContext, newContext)
        )
      );
      throwAggregateErrorFromPromiseResults(results, this.providerEntries);
    }

    override get hooks(): Hook[] {
      return [
        ...super.hooks,
        {
          before: (hookContext, hints) => {
            const hinted = hints?.[FLAG_AUTHORITY_HINT];
            if (typeof hinted === 'string') {
              declaredAuthority.set(hookContext.context, hinted);
              if (!authorityNames.includes(hinted) && !warnedHints.has(hinted)) {
                warnedHints.add(hinted);
                logger.warn('Unknown flag authority hint, using the default authority', {
                  hint: hinted,
                  defaultAuthority: authorityNames[0],
                });
              }
            }
          },
        },
      ];
    }
  }

  const provider = new ComparisonMultiProvider(
    entries,
    new CustomComparisonStrategy(authorityNames, shadow?.name, (context) =>
      declaredAuthority.get(context)
    )
  );
  provider.metadata = { ...provider.metadata, name: 'ComparisonProvider' };
  Object.defineProperty(provider, 'flagsmithClient', {
    get: () =>
      authorities
        .map(
          (authority) =>
            (authority.provider as { flagsmithClient?: unknown }).flagsmithClient
        )
        .find(Boolean),
  });
  return provider;
}

class CustomComparisonStrategy extends BaseEvaluationStrategy<ProviderStatus, Provider> {
  override runMode = 'parallel' as const;

  constructor(
    private readonly authorityNames: string[],
    private readonly shadowName: string | undefined,
    private readonly getDeclaredAuthority: (
      context: EvaluationContext
    ) => string | undefined
  ) {
    super(ProviderStatus);
  }

  override shouldEvaluateThisProvider(
    strategyContext: StrategyPerProviderContext,
    context?: EvaluationContext
  ): boolean {
    // Authorities other than the declared one are never evaluated.
    if (
      this.authorityNames.includes(strategyContext.providerName) &&
      strategyContext.providerName !== this.intendedAuthority(context)
    ) {
      return false;
    }

    return super.shouldEvaluateThisProvider(strategyContext, context);
  }

  override determineFinalResult<T extends FlagValue>(
    _strategyContext: StrategyEvaluationContext,
    context: EvaluationContext,
    providerResults: ProviderResult<T>[]
  ): FinalResult<T> {
    const shadow = providerResults.find(
      (result) => result.providerName === this.shadowName
    );
    const intendedName = this.intendedAuthority(context);
    const intended = providerResults.find(
      (result) => result.providerName === intendedName
    );

    if (this.isHealthy(intended)) {
      const outcome: ComparisonOutcome = {};

      if (this.isHealthy(shadow)) {
        const mismatch = !this.valuesEqual(intended.details.value, shadow.details.value);
        // Compare values independently of reasons; reason drift is reported separately.
        const reasonDiverged = intended.details.reason !== shadow.details.reason;
        Object.assign(outcome, {
          agreement: !mismatch || undefined,
          mismatch: mismatch || undefined,
          shadowValue: mismatch ? serialiseValue(shadow.details.value) : undefined,
          shadowReason: mismatch || reasonDiverged ? shadow.details.reason : undefined,
        });
      } else if (this.errorCodeOf(shadow) === ErrorCode.PROVIDER_NOT_READY) {
        outcome.shadowPending = true;
      } else if (shadow) {
        outcome.failedProvider = shadow.providerName;
        outcome.failedCode = this.errorCodeOf(shadow);
      }

      return this.withMetadata(intended, outcome);
    }

    if (this.isHealthy(shadow)) {
      return this.withMetadata(shadow, {
        failedProvider: intendedName,
        failedCode: this.errorCodeOf(intended),
      });
    }

    if (this.isFlagMissing(intended) && (!shadow || this.isFlagMissing(shadow))) {
      throw new FlagNotFoundError();
    }

    return this.collectProviderErrors(providerResults);
  }

  private intendedAuthority(context?: EvaluationContext): string {
    const declaredName = context ? this.getDeclaredAuthority(context) : undefined;
    return declaredName !== undefined && this.authorityNames.includes(declaredName)
      ? declaredName
      : this.authorityNames[0]!;
  }

  protected valuesEqual(a: FlagValue, b: FlagValue): boolean {
    return isEqual(a, b);
  }

  private isHealthy<T extends FlagValue>(
    result: ProviderResult<T> | undefined
  ): result is HealthyResult<T> {
    return result !== undefined && !this.hasError(result);
  }

  private isFlagMissing<T extends FlagValue>(
    result: ProviderResult<T> | undefined
  ): boolean {
    return (
      result !== undefined && this.hasErrorWithCode(result, ErrorCode.FLAG_NOT_FOUND)
    );
  }

  // No `thrownError` branch needed: MultiProvider normalises a throw into this
  // code before the strategy sees it.
  private errorCodeOf<T extends FlagValue>(
    result: ProviderResult<T> | undefined
  ): string | undefined {
    return (result as { details?: { errorCode?: ErrorCode } } | undefined)?.details
      ?.errorCode;
  }

  private withMetadata<T extends FlagValue>(
    winner: HealthyResult<T>,
    outcome: ComparisonOutcome
  ): FinalResult<T> {
    const findings = Object.fromEntries(
      Object.entries(outcome).filter(([, finding]) => finding !== undefined)
    );

    return {
      provider: winner.provider,
      providerName: winner.providerName,
      details: {
        ...winner.details,
        flagMetadata: {
          ...winner.details.flagMetadata,
          ...findings,
          provider: winner.providerName,
        },
      },
    };
  }
}
